import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    inject,
    InjectionToken,
    Input,
    model,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DomMutation } from 'shared/dom-mutation-core';
import { IDisplaySlide, SlideType } from '@pcp/core/interfaces';
import { assertNotNullOrUndefined } from '@pcp/core/util';
import { UpdateSlideContentCommand } from '@pcp/streaming';
import { map, Observable, ReplaySubject, Subscription, switchMap, tap } from 'rxjs';
import { WindowDomMutationMessage } from '../interfaces';


export const SLIDE_SYNC_HTML_ELEMENT_BASE_URL = new InjectionToken<string>('SLIDE_SYNC_HTML_ELEMENT_BASE_URL');

/**
 * Display of Slides having Type "I" - interactive element slides.
 */
@Component({
    selector: 'pcp-html-element-board',
    templateUrl: './html-element-board.component.html',
    styleUrls: ['./html-element-board.component.scss'],
    imports: []
})
export class HtmlElementBoardComponent implements OnDestroy, AfterViewInit {
    /**
     * Origin of any interactive HTML-Element displayed inside the iFrame.
     * This component will listen on window:message event coming from
     * this origin only.
     */
    private readonly sanitizer = inject(DomSanitizer);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly trustedOrigin = inject(SLIDE_SYNC_HTML_ELEMENT_BASE_URL);

    #htmlElement: IDisplaySlide;
    public trustedUrl: null | SafeResourceUrl = null;

    /**
     * Emits the iframe ELEMENT each time its content document has loaded — not when
     * the element is created. `postMessage` into an iframe whose document hasn't run
     * its injected sync script yet is silently dropped, and the buffered mutation
     * backlog (late-join / beamer reload / lesson-start carry-over) is posted the
     * moment this gate opens, so opening it at element creation loses the whole
     * backlog.
     *
     * Carries the element (buffer 1) rather than a void tick: the update pipeline
     * must post to the frame that actually loaded, never to the `@ViewChild`
     * property — its set/unset lifecycle is not synchronized with DOM `load` events.
     */
    private readonly iframeLoaded = new ReplaySubject<HTMLIFrameElement>(1);
    private updateDomSubscription?: Subscription;
    private resizeObserver?: ResizeObserver;

    // Fixed iframe dimensions for consistent rendering across all instances
    readonly fixedIframeWidth = 1920;
    readonly fixedIframeHeight = 1080;

    // Dynamic scale values for CSS transform
    readonly scaleX = model(1);
    readonly scaleY = model(1);
    readonly scaleTransform = computed(() => `scale(${this.scaleX()}, ${this.scaleY()})`);

    /** If false, no DOM modifications (interactive element interactions) are emitted. */
    @Input() emitModifications = false;

    @HostBinding('class.fullscreen')
    @Input() isPresentationScreen: boolean = false;

    /** If true, all event listeners on the element will be removed, effectively making the element read only */
    @Input() disableElementUsage: boolean = false;

    @Input() set updateInteractiveElement(updates: undefined | Observable<UpdateSlideContentCommand>) {
        this.updateDomSubscription?.unsubscribe();
        if (updates === undefined) {
            return;
        }

        this.updateDomSubscription = this.iframeLoaded
            .pipe(
                // Deliberately NOT take(1): the frame's FIRST `load` can be its
                // transient `about:blank` document (fires before the `[src]`
                // document arrives), which would swallow a one-shot gate — the
                // buffered backlog would be posted into a listener-less blank page
                // and never reach the real element. Re-subscribing the update
                // stream on EVERY load replays the full buffered backlog into each
                // new document exactly once (each load = a fresh document that has
                // seen nothing), while live commands ride the latest subscription.
                switchMap(frame => updates.pipe(map(u => ({ frame, mutations: u.mutations })))),
                tap(({ frame, mutations }) => {
                    // contentWindow is a stable WindowProxy that reaches the CURRENT
                    // document even across an iframe reload; null only when the
                    // element has been removed from the DOM (nothing left to update).
                    frame.contentWindow?.postMessage({
                        action: 'updateDOM',
                        updatedDom: mutations,
                    }, '*');
                }),
            )
            .subscribe();
    }

    @Output() domModified = new EventEmitter<DomMutation[]>();

    htmlElementFrame: ElementRef<HTMLIFrameElement>;
    @ViewChild('htmlElementFrame') set _htmlElementFrame(iframe: ElementRef<HTMLIFrameElement>) {
        this.htmlElementFrame = iframe;
    }

    /** `(load)` handler — see {@link iframeLoaded} for why readiness is load-gated. */
    onIframeLoaded(frame: HTMLIFrameElement): void {
        this.iframeLoaded.next(frame);
    }

    ngAfterViewInit(): void {
        // Set up dynamic scaling based on container size
        this.setupDynamicScaling();
    }

    ngOnDestroy(): void {
        this.updateDomSubscription?.unsubscribe();
        this.resizeObserver?.disconnect();
    }


    /**
     * Sets up ResizeObserver to dynamically calculate scaling based on container size changes.
     */
    private setupDynamicScaling(): void {
        // Update immediately
        this.updateScaling();

        // Set up ResizeObserver to track component size changes
        this.resizeObserver = new ResizeObserver(() => {
            this.updateScaling();
        });

        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    /**
     * Updates CSS scale transform based on current container size.
     * Scales the fixed-size iframe to fit the container while maintaining aspect ratio.
     */
    private updateScaling(): void {
        // Use the host element (pcp-html-element-board) as container reference
        const hostElement = this.elementRef.nativeElement;

        if (!hostElement) return;

        const containerWidth = hostElement.clientWidth;
        const containerHeight = hostElement.clientHeight;

        // Skip if container has no size yet
        if (containerWidth === 0 || containerHeight === 0) return;

        // Calculate scale ratios
        const scaleX = containerWidth / this.fixedIframeWidth;
        const scaleY = containerHeight / this.fixedIframeHeight;

        // Use uniform scaling (same scale for both X and Y) to maintain aspect ratio
        const uniformScale = Math.min(scaleX, scaleY);


        this.scaleX.set(uniformScale);
        this.scaleY.set(uniformScale);
    }

    @Input()
    set htmlElement(el: IDisplaySlide) {
        switch (el.type) {
            case SlideType.interactive:
                const src = assertNotNullOrUndefined(el?.interactiveElementUrl);
                this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(src);
                break;
            case SlideType.rubber:
                break;
        }

        this.#htmlElement = el;
    }

    get htmlElement(): IDisplaySlide {
        return this.#htmlElement;
    }

    /**
     * Received message from the iFrame
     * including the updated DOM.
     */
    @HostListener('window:message', ['$event']) onMessageFromIframe(
        event: MessageEvent<WindowDomMutationMessage | any>,
    ): void {

        if (this.emitModifications === false || event.origin !== this.trustedOrigin) {
            return;
        }

        // Only listen to messages from the original HtmlElement, not from the clone.
        // Optional-chained: the ViewChild property's set/unset lifecycle is not
        // synchronized with window message timing (same decoupling as `iframeLoaded`).
        if (this.htmlElementFrame?.nativeElement?.contentWindow !== event.source) {
            return;
        }

        if (event.data?.updatedDom && Array.isArray(event.data.updatedDom)) {
            this.domModified.emit(event.data?.updatedDom);
        }
    }

    /**
     * Resets the HTML element by reloading the iframe.
     * This ensures that any modifications made to the interactive element are cleared.
     *
     * Uses URL mutation instead of clearing src to avoid iOS Safari iframe blank bug.
     * iOS Safari destroys iframe context when src is set to empty, unlike desktop Safari.
     */
    reset(): void {
        if (this.htmlElementFrame?.nativeElement && this.trustedUrl) {
            const iframe = this.htmlElementFrame.nativeElement;
            const currentSrc = iframe.src;

            // Add timestamp parameter to force reload without clearing src
            const url = new URL(currentSrc);
            url.searchParams.set('_reload', Date.now().toString());
            iframe.src = url.toString();
        }
    }
}
